import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createEditor, Descendant, Editor, Range, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, useSlateStatic, withReact } from 'slate-react';
import { appNote } from '../constants';
import { computeLength, IMentionProps, mapDescendantsToText, mapTextToDescendants, MentionElement } from './models';
import { UtilityService } from '../../../services/UtilityService';
import { Popover } from '@mui/material';
import { useSelector } from 'react-redux';
import DateAndTimePicker from '../datetimepicker/date-timepicker.component';
import * as selector from "../../../selectors/selectors"
import { UserPlanTracker } from '../../../models/plan-metrics.model';
const MentionComponent = (props: IMentionProps) => {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const [popoverAnchor, setPopoverAnchor] = useState<{ top: number; left: number } | null>(props.PopoverAnchor);
    const [value, setValue] = useState<Descendant[]>([])
    const [target, setTarget] = useState<Range | undefined | null>()
    const [search, setSearch] = useState('')
    const [showTags, setShowTags] = useState(props.openTags);
    const [showingCategoryIndex, setShowingCategoryIndex] = useState<number>(0);
    const [isTouched, setIsTouched] = useState<boolean>(false)
    const renderElement = useCallback(innerProps => <Element {...innerProps} tags={props.tags} IsOutbound={props.IsOutbound} spellCheck={true} Editor={editor} />, [])
    const IsDarkMode: boolean = useSelector(selector.isDarkMode)
    const IsNewOrgFlow: boolean = useSelector(selector.isNewOrgFlow);
    const UserPlanTracker: UserPlanTracker = useSelector(selector.userPlanTracker)

    const editor = useMemo(() => {
        const _editor = withMentions(withReact(withHistory(createEditor() as any)))
        const { isVoid } = _editor;
        _editor.isVoid = (el) => el.id != null || isVoid(el);
        return _editor
    }, [])

    const chars = search ? props.tags.map(x => ({
        ...x, tags: x.tags.filter(c =>
            c.id.toLowerCase().startsWith(search.toLowerCase())
        ).slice(0, 10)
    })).filter(x => x.tags.length) : props.tags.slice(0, 10)

    const allTags = props.tags.reduce((p, c) => [...p, ...c.tags], [])

    useEffect(() => {
        if (props.IsGroupChat) {
            setSearch("")
            setTarget(null)
        }
        if (chars.length <= showingCategoryIndex) {
            setShowingCategoryIndex(0)
        }
    }, [chars.length, editor])

    useEffect(() => {
        if (props.emoji) {
            Editor.insertText(editor, props.emoji)
        }
    }, [props.emoji])

    useEffect(() => {
        if (!props.body) {
            clearEditor();
            editor.children = [];
            Transforms.insertNodes(editor, [{ type: "paragraph", children: [{ text: "" }] } as any])
        }
        else {
            let des = mapTextToDescendants(props.body, allTags, props.IsLinkAllowed);
            let text = mapDescendantsToText(value);

            if (text !== props.body) {
                clearEditor();
                editor.children = [];
                Transforms.insertNodes(editor, des)
            }
        }
    }, [props.body]);

    const clearEditor = () => {
        const editor_selection = editor.selection;
        if (!editor_selection) {
            return;
        }

        const [, first_path] = Editor.first(editor, [0]);
        const [, last_path] = Editor.last(editor, [
            editor.children.length - 1,
        ]);

        const full_range = Editor.range(
            editor,
            first_path,
            last_path
        );

        full_range.focus.offset = full_range.focus.offset === 0 ? 1 : full_range.focus.offset;
        Transforms.setSelection(editor, full_range)
        const is_everything_selected = Range.equals(editor.selection, full_range);

        if (is_everything_selected) {
            Transforms.removeNodes(editor);
            return;
        }
    }

    const handleOutsideClick = (event: any) => {
        event?.stopPropagation()
        props.closeTags()
        setShowTags(false)
        setTarget(null)
    }

    const handleKeyDown = (event: any) => {
        event?.stopPropagation();
        if (event.key === "Escape") {
            setTarget(null)
            props.closeTags()
        }
    }

    useEffect(() => {
        setShowTags(props.openTags)
    }, [props.openTags])


    useEffect(() => {
        const handleClickOutside = (e) => {
            handleOutsideClick(e);
        };

        document.addEventListener("click", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleOutsideClick]);

    const getDisabledTags = (char: any) => {
        if (char.className === "user") {
            if (char.id === "MyFirstName" || char.id === "MyLastName" || char.id === "MyEmail") {
                return "disabled"
            } else {
                return char.className
            }
        } else {
            return char.className
        }
    }
    const handleChange = useCallback(() => {
        const { selection } = editor;
        if (selection && Range.isCollapsed(selection) && !props.IsGroupChat && isTouched && (IsNewOrgFlow ? UserPlanTracker.HasCustomTags : true)) {
            const [start] = Range.edges(selection);
            const beforeText = Editor.string(editor, { anchor: { path: start.path, offset: 0 }, focus: start, });

            const atIndex = beforeText.lastIndexOf("@");

            if (atIndex !== -1) {
                const afterAtText = beforeText.slice(atIndex + 1);
                let text = beforeText.slice(atIndex)

                let filtertags = text ? props.tags.map(x => ({
                    ...x, tags: x.tags.filter(c =>
                        c.id.toLowerCase().startsWith(text?.replace("@", "").toLowerCase())
                    ).slice(0, 10)
                })).filter(x => x.tags.length) : props.tags.slice(0, 10)

                if (!!text || afterAtText.length === 0) {
                    setTarget(selection);
                    if (!!filtertags?.length) {
                        setSearch(text?.replace("@", ""))
                        setTimeout(() => {
                            const domSelection = window.getSelection();
                            if (domSelection && domSelection.rangeCount > 0) {
                                const range = domSelection.getRangeAt(0);
                                const rect = range.getBoundingClientRect();

                                if (rect && rect.width !== 0 && rect.height !== 0) {
                                    setPopoverAnchor({
                                        top: rect.bottom + window.scrollY,
                                        left: rect.left + window.scrollX,
                                    });
                                }
                            }
                        }, 10);
                    }
                    else {
                        setSearch("")
                        // console.log("Closing popover since no text under", search);
                        setTarget(null);
                        setPopoverAnchor(null);
                    }
                } else {
                    //console.log("Closing popover since more text is entered", search);
                    setTarget(null);
                    setPopoverAnchor(null);
                }
            } else {
                //console.log("Closing popover, '@' not found");
                setTarget(null);
                setPopoverAnchor(null);
            }
        }
    }, [editor, props.IsGroupChat, isTouched]);

    useEffect(() => {
        setPopoverAnchor(props.PopoverAnchor)
    }, [props.PopoverAnchor])

    useEffect(() => {
        setIsTouched(false)
        setShowTags(false)
        setTarget(null)
    }, [props.phoneNumbers])

    return (
        <Slate
            editor={editor}
            initialValue={value}
            onChange={value => {
                setValue(value)
                handleChange()
                props.onChange({
                    target: {
                        value: mapDescendantsToText(value) || ""
                    }
                }, computeLength(value))
            }}
        >
            <div ref={editorRef}> <Editable
                renderElement={(params) => renderElement({ ...params, index: showingCategoryIndex })}
                onKeyDown={(e) => { props.onKeyDown(e); setIsTouched(true) }}
                style={{ lineHeight: props.lineHeight ? props.lineHeight : 1.5, overflowWrap: "anywhere" }}
                placeholder={props.placeholder}
                maxLength={props.maxLength}
                tabIndex={11}
                readOnly={!!props.readOnly}
                onBlur={props.onBlur}
                spellCheck={true}

            />
            </div>
            <Popover
                open={showTags ? showTags : !!target}
                anchorReference="anchorPosition"
                anchorPosition={popoverAnchor ?? { top: 0, left: 0 }}
                onClose={() => {
                    setTarget(null);
                    setShowTags(false)
                }}
                anchorOrigin={showTags ? {
                    vertical: 'top',
                    horizontal: 'center',
                } : {
                    vertical: "top",
                    horizontal: "left",
                }}
                transformOrigin={showTags ? {
                    vertical: 'bottom',
                    horizontal: 'center',
                } : {
                    vertical: "bottom",
                    horizontal: "left",
                }}
                disableAutoFocus
                disableRestoreFocus
                disableEnforceFocus
                slotProps={{
                    root: { sx: { pointerEvents: "none" } },
                    paper: { sx: { pointerEvents: "auto" } }
                }}
            >
                <div className={`h4-regular w-32 ${IsDarkMode ? "bg-color-sidebar-bg" : ""}`} >
                    <div className='color-tags-title h4-regular px-1 pt-1'>{appNote.CUSTOM_TAGS}</div>
                    <div className='d-flex justify-around'>
                        {
                            chars.map((char, i) => (
                                <div key={i}>
                                    <div className=''>
                                        <div className={`px-1 pt-1 c-pointer  h5-regular ${showingCategoryIndex === i ? "color-tags-title" : "color-tags-title-unselect"} `} onClick={(e) => { e.stopPropagation(); setShowingCategoryIndex(i); }}>{char.label} </div>
                                    </div>
                                </div>))
                        }
                    </div>
                    {!search && <div className="d-flex mx-1">
                        <div className={`show-conversation-animation  show-conversation-border show-conversation-height bg-color-primary-light ${showingCategoryIndex === 0 ? "ml-p-0 w-p-53" : "ml-p-62 w-p-36"}  `}></div>
                    </div>}
                    <div className='bb-primary-divider-2'></div>

                    {<div className={"d-flex flex-wrap footnote justify-between p-1 h-12"}>   {chars[showingCategoryIndex]?.tags?.map((char, i) => (
                        <div
                            key={char.id}
                            className={`c-pointer tag my-auto ${props.IsAddRule ? getDisabledTags(char) : char.className}`}
                            onClick={(event: any) => {
                                event.preventDefault(); event.stopPropagation();
                                if (target) {
                                    Transforms.delete(editor, { unit: "character", distance: search.length + 1, reverse: true })
                                    insertMention(editor, chars[showingCategoryIndex].tags[i])
                                    setSearch("")
                                    setTarget(null)
                                }
                                else if (props.openTags) {
                                    if (!props.openTags) {
                                        if (UtilityService.isAndroid(navigator.userAgent) && mapDescendantsToText(value).slice(-1) === "@") {
                                            let bodyText = mapDescendantsToText(value).slice(0, -1);
                                            let desc = mapTextToDescendants(bodyText, allTags, props.IsLinkAllowed);
                                            clearEditor();
                                            editor.children = [];
                                            Transforms.insertNodes(editor, desc)
                                        }
                                        else {
                                            Transforms.delete(editor, { unit: "character", distance: search.length + 1, reverse: true })
                                        }
                                    }
                                    insertMention(editor, chars[showingCategoryIndex].tags[i])
                                    setSearch("")
                                    setTarget(null)
                                    setShowTags(false)
                                    props.closeTags()
                                }
                            }}
                        >
                            {chars[showingCategoryIndex].tags[i].display}
                        </div>
                    ))}
                    </div>}
                </div>
            </Popover>
        </Slate>
    )
}

const withMentions = (editor: any) => {
    const { isInline, isVoid } = editor

    editor.isInline = (element: any) => {
        return element.type === 'mention' || element.type === 'link' || element.type === 'mail' ? true : isInline(element)
    }

    editor.isVoid = (element: any) => {
        return element.type === 'mention' || element.type === 'link' || element.type === 'mail' ? true : isVoid(element)
    }

    return editor
}

const insertMention = (editor: any, tag: { id: string; display: string; length: number; className: string }) => {
    const mention: MentionElement = {
        type: 'mention',
        character: tag.id,
        display: tag.display,
        length: tag.length,
        children: [{ text: '' }],
        className: tag.className ?? ""
    }
    Transforms.insertNodes(editor, mention as any)
    Transforms.move(editor)
}

const Element = (props: any) => {
    const { attributes, children, element, tags, IsOutbound, spellCheck } = props;
    const IdDarkMode: boolean = useSelector(selector.isDarkMode)

    switch (element.type) {
        case 'mention':
            return <Mention {...props} tags={tags} Editor={props.Editor} />
        case 'link':
            let regex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gm
            let url = element.character.match(regex) ? element.character : `http://${element.character}`
            return <a
                style={{ cursor: "pointer" }}
                href={url}
                className={` ${(IsOutbound && IdDarkMode) ? "" : " color-text-field-focused"} t-decoration-underline`}
                target="_blank" rel="noopener noreferrer"
            >
                {element.character}
            </a>
        case 'mail':
            return <a
                style={{ cursor: "pointer" }}
                href={`mailto: ${element.character}`}
                className={` ${(IsOutbound && IdDarkMode) ? "" : " color-text-field-focused"} t-decoration-underline`}
                target="_blank" rel="noopener noreferrer"
            >
                {element.character}
            </a>
        default:
            return <p className='my-auto' {...attributes} spellCheck={spellCheck}>{children}</p>
    }
}

const Mention = ({ attributes, children, element, tags, IsOutbound, Editor }: any) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedDateTime, setSelectTimeDate] = useState()
    const [isDate, setIsdate] = useState<boolean>(false)

    const handleClick = (event: React.MouseEvent<HTMLElement>, character) => {
        event.stopPropagation();

        if (character === "Date" || character === "DateTime") {
            setAnchorEl(event.currentTarget);
            if (character === "Date") {
                setIsdate(true)
            }
        }
    };

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleSelectTimeDate = (dateTime) => {
        setSelectTimeDate(dateTime)
        const text = isDate ? " " + UtilityService.convertToDateWithWeekFormat(dateTime).toString() + " " : " " + UtilityService.convertToDateTimeFormat(dateTime).toString() + " "
        const path = ReactEditor.findPath(Editor, element)
        Transforms.removeNodes(Editor, { at: path })
        Transforms.insertNodes(Editor, { text } as any, { at: path, select: true })
    }

    const allTags = tags.reduce((p, c) => [...p, ...c.tags], [])
    const normalizedChar = (element.character || "").replaceAll(" ", "")
    const foundTag = allTags.find((x: any) => (x.id || "").replaceAll(" ", "") === normalizedChar)
    const tagClassName = foundTag?.className ?? "contact"
    const resolvedClassName = (tagClassName === "contact" && IsOutbound) ? "bg-color-secondary" : tagClassName
    return (
        <> <span
            {...attributes}
            contentEditable={false}
            data-cy={`mention-${element.character.replace(' ', '-')}`}
            className={`tag ${resolvedClassName}`}
            onClick={(e) => handleClick(e, element?.character)}
        >
            {foundTag?.display || element.character}
            {children}
        </span>
            {
                Boolean(anchorEl) &&
                <DateAndTimePicker
                    isMessage={Boolean(anchorEl)}
                    CloseCalender={() => {
                        handleClose();
                    }}
                    event={anchorEl}
                    handleChange={handleSelectTimeDate}
                    DateTime={selectedDateTime}
                    IsMobileDevice={false}
                    anchorEl={anchorEl}
                    HideTime={element.character === "Date"}
                    ShowMinData={true}
                />
            }
        </>
    )
}

export default MentionComponent